import { useMemo } from 'react';
import { useTranslation } from '@/hooks/use-translation';
import { DashboardEntryData } from '../types';
import { useDashboardContext } from './use-dashboard-context';

type DashboardEntryDataSelector = (
  data: DashboardEntryData[]
) => DashboardEntryData | null;

export type UseDashboardIndicatorProps = {
  select: string | DashboardEntryDataSelector;
  displayValue?: (data: DashboardEntryData) => React.ReactNode;
  displayLabel?: string | ((indicator: DashboardEntryData) => React.ReactNode);
  displayDescription?:
    | string
    | ((indicator: DashboardEntryData) => React.ReactNode);
};

export function useDashboardIndicator(props: UseDashboardIndicatorProps) {
  const { t } = useTranslation();

  const {
    select,
    displayValue = (data) => String(data.value),
    displayLabel = (data) =>
      t(`dashboardIndicator.${data.metric}`, data.metric),
    displayDescription = null,
  } = props;

  const { data, loading } = useDashboardContext();

  const indicator = useMemo(() => {
    if (!data) return null;

    if (typeof select === 'function') {
      return select(data) || null;
    }

    return data.find((entry) => entry.metric === select) || null;
  }, [select, data]);

  const display = useMemo(() => {
    if (!indicator) return null;
    return {
      value: displayValue(indicator),
      label:
        typeof displayLabel === 'function'
          ? displayLabel(indicator)
          : displayLabel,
      description:
        typeof displayDescription === 'function'
          ? displayDescription(indicator)
          : displayDescription,
    };
  }, [indicator, displayValue, displayLabel, displayDescription]);

  return { display, loading, data, current: indicator };
}
